import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, ClipboardPaste, FolderOpen } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Emblema, Seccion } from '@/components/Card';
import { Campo, TextField, clasesCon } from '@/components/Field';
import { importarRutina, revisarRutinaDeArchivo, type RutinaParaImportar } from '@/db/rutinas-archivo';
import { mensajeDeError } from '@/db/tx';
import { nuevoId } from '@/db/ids';
import { textoObjetivo } from '@/domain/entrenamiento';
import { ETIQUETAS_MODALIDAD } from '@/domain/modalidad';
import { ETIQUETA_VINCULO } from '@/domain/rutina-archivo';
import { textoDias } from '@/domain/sesiones';
import { leerArchivoComoTexto } from '@/lib/file-transfer';

type Estado =
  | { fase: 'inicio' }
  | { fase: 'pegando' }
  | { fase: 'leyendo' }
  | { fase: 'revisada'; listo: Extract<RutinaParaImportar, { ok: true }>; nombre: string }
  | { fase: 'guardada'; id: string; nombre: string };

/**
 * Traer una rutina de un archivo (Req. 27.2, 27.3).
 *
 *   elegir archivo o pegar el texto → ver qué trae → guardar
 *
 * Nada se guarda hasta el último toque, y solo se agrega esa rutina con los
 * ejercicios que falten: ninguna otra cosa cambia.
 */
export function ImportarRutinaPage() {
  const navegar = useNavigate();
  const entrada = useRef<HTMLInputElement>(null);
  const [estado, setEstado] = useState<Estado>({ fase: 'inicio' });
  const [pegado, setPegado] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  // El id se crea una vez: dos toques no crean dos rutinas
  const [id] = useState(() => nuevoId('rut'));

  async function revisar(texto: string) {
    setError(null);
    setEstado({ fase: 'leyendo' });
    const r = await revisarRutinaDeArchivo(texto);
    if (!r.ok) {
      setError(r.error);
      setEstado({ fase: 'inicio' });
      return;
    }
    setEstado({ fase: 'revisada', listo: r, nombre: r.plan.nombre });
  }

  async function elegir(archivo: File) {
    setError(null);
    setEstado({ fase: 'leyendo' });
    try {
      await revisar(await leerArchivoComoTexto(archivo));
    } catch (e) {
      setError(mensajeDeError(e));
      setEstado({ fase: 'inicio' });
    }
  }

  async function guardar(e: Extract<Estado, { fase: 'revisada' }>) {
    if (guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const nueva = await importarRutina(e.listo.archivo, e.nombre, id);
      setEstado({ fase: 'guardada', id: nueva, nombre: e.nombre });
    } catch (x) {
      setError(mensajeDeError(x));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Traer una rutina."
        descripcion="De un archivo que te compartieron. Verás qué trae antes de guardarla."
        volver={{ a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' }}
      />

      <input
        ref={entrada}
        type="file"
        accept="application/json,.json"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          if (archivo !== undefined) void elegir(archivo);
        }}
      />

      {estado.fase === 'inicio' && (
        <Card>
          <Emblema icono={FolderOpen} />
          <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">Abrir el archivo</h2>
          <p className="text-[13px] leading-relaxed text-texto-suave">
            El archivo termina en <strong>.rutina.json</strong>. Si te lo mandaron por WhatsApp, guárdalo primero en
            Archivos. Si te lo pasaron como texto, pégalo aquí abajo.
          </p>
          <div className="mt-5 grid gap-2">
            <Button variante="secundario" icono={FolderOpen} onClick={() => entrada.current?.click()}>
              Elegir un archivo de rutina
            </Button>
            <Button variante="fantasma" icono={ClipboardPaste} onClick={() => setEstado({ fase: 'pegando' })}>
              Pegar el texto
            </Button>
          </div>
        </Card>
      )}

      {estado.fase === 'pegando' && (
        <Card className="space-y-4">
          <Campo etiqueta="El texto de la rutina" ayuda="Pégalo completo, tal como te lo mandaron.">
            {({ id, describedBy, hayError }) => (
              <textarea
                id={id}
                aria-describedby={describedBy}
                value={pegado}
                onChange={(e) => setPegado(e.target.value)}
                rows={6}
                maxLength={60_000}
                className={`${clasesCon(hayError)} min-h-32 resize-y py-3 font-mono text-[13px] leading-relaxed`}
                placeholder='{ "app": "app-gym-salud", "tipo": "rutina", …'
              />
            )}
          </Campo>
          <div className="grid gap-2">
            <Button disabled={pegado.trim() === ''} onClick={() => void revisar(pegado)}>
              Revisar lo pegado
            </Button>
            <Button variante="fantasma" onClick={() => setEstado({ fase: 'inicio' })}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {estado.fase === 'leyendo' && (
        <Banner tono="info" titulo="Revisando el archivo">
          Se comprueba qué trae. Todavía no se guarda nada.
        </Banner>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo abrir">
          {error}
        </Banner>
      )}

      {estado.fase === 'revisada' && (
        <>
          <Seccion
            titulo="Esto trae el archivo"
            descripcion="Nada se guarda hasta que toques Guardar. Tus otras rutinas, tus ejercicios y tus entrenamientos no cambian."
            extra={`${estado.listo.plan.ejercicios.length} ejercicios`}
          >
            <div className="space-y-4">
              <TextField
                etiqueta="Nombre con que se guarda"
                valor={estado.nombre}
                onCambio={(v) => setEstado({ ...estado, nombre: v })}
                maxLength={60}
              />
              <p className="text-[12px] leading-relaxed text-texto-suave">
                {[
                  `Se guarda en ${ETIQUETAS_MODALIDAD[estado.listo.archivo.rutina.modalidad ?? 'gimnasio']}`,
                  textoDias(estado.listo.archivo.rutina.dias) || 'sin días fijos',
                ].join(' · ')}
                .
              </p>
              {estado.listo.archivo.rutina.notas !== null && (
                <p className="text-[13px] leading-relaxed text-texto-suave">{estado.listo.archivo.rutina.notas}</p>
              )}
              {estado.listo.plan.advertencias.map((a) => (
                <Banner key={a} tono="info" titulo={a} />
              ))}
              <ul className="divide-y divide-borde">
                {estado.listo.plan.ejercicios.map((v, i) => (
                  <li key={`${v.del.exerciseId}-${i}`} className="py-3">
                    <p className="text-[14px] font-medium text-texto">{v.nombre}</p>
                    <p className="text-[12px] text-texto-suave">
                      {[
                        textoObjetivo(v.del.series, v.del.repsMin, v.del.repsMax, v.del.porLado === true),
                        v.del.biserie == null ? null : `biserie ${v.del.biserie}`,
                        ETIQUETA_VINCULO[v.como],
                      ]
                        .filter((x) => x !== null)
                        .join(' · ')}
                    </p>
                    {v.del.notas !== null && <p className="text-[12px] text-texto-suave">{v.del.notas}</p>}
                    {v.nota !== null && <p className="text-[12px] text-aviso">{v.nota}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </Seccion>
          <div className="grid gap-2">
            <Button icono={Check} disabled={guardando} onClick={() => void guardar(estado)}>
              {guardando ? 'Guardando…' : 'Guardar esta rutina'}
            </Button>
            <Button variante="fantasma" onClick={() => setEstado({ fase: 'inicio' })}>
              No guardarla
            </Button>
          </div>
        </>
      )}

      {estado.fase === 'guardada' && (
        <>
          <Banner tono="exito" titulo={`Guardada: ${estado.nombre}`}>
            Ya está en tus rutinas, con sus ejercicios. Puedes cambiarle lo que quieras: es tuya.
          </Banner>
          <div className="grid gap-2">
            <Button icono={Check} onClick={() => void navegar(`/gym/rutina/${estado.id}`)}>
              Ver la rutina
            </Button>
            <Button variante="fantasma" onClick={() => void navegar('/gym')}>
              Volver a Gym
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
